import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import React from "react";
import { useTranslationApp } from "../../../../lib/i18next";
import { useDispatchApp } from "../../../../lib/redux";
import {
  AUTOMATIONS_CONNECT_ACCOUNTS,
  AUTOMATIONS_PLATFORM_CONNECTED,
  AUTOMATIONS_STEP_PLATFORMS_HELP,
  AUTOMATIONS_STEP_PLATFORMS_TITLE,
  CONNECTED_ACCOUNTS_EMPTY_HINT,
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN,
  TIKTOK,
  YOUTUBE,
} from "../../../../i18n/keysTranslations";
import { startGetIntegrations } from "../../../../actions/user";
import { useSharePlatformAccounts } from "../../../../hooks/useSharePlatformAccounts";
import useButtonActionPlatform from "../../../../hooks/useButtonActionPlatform";
import PlatformLogo from "../../../Common/PlatformLogo";
import ButtonMenu from "../../../Buttons/ButtonMenu";
import AutomationStepSection from "../AutomationStepSection";
import { subtleBorderSx } from "../automationModalStyles";
import {
  FACEBOOK as FACEBOOK_PLATFORM,
  INSTAGRAM as INSTAGRAM_PLATFORM,
  LINKEDIN as LINKEDIN_PLATFORM,
  META,
  TIKTOK as TIKTOK_PLATFORM,
  YOUTUBE as YOUTUBE_PLATFORM,
} from "../../../../utils/constants";

const PLATFORM_NAME_KEYS = {
  instagram: INSTAGRAM,
  facebook: FACEBOOK,
  tiktok: TIKTOK,
  youtube: YOUTUBE,
  linkedIn: LINKEDIN,
};

const CONNECT_ACCOUNT_OPTIONS = [
  {
    platform: INSTAGRAM_PLATFORM,
    actionKey: META,
    label: INSTAGRAM,
  },
  {
    platform: FACEBOOK_PLATFORM,
    actionKey: META,
    label: FACEBOOK,
  },
  {
    platform: TIKTOK_PLATFORM,
    actionKey: TIKTOK_PLATFORM,
    label: TIKTOK,
  },
  {
    platform: YOUTUBE_PLATFORM,
    actionKey: YOUTUBE_PLATFORM,
    label: YOUTUBE,
  },
  {
    platform: LINKEDIN_PLATFORM,
    actionKey: LINKEDIN_PLATFORM,
    label: LINKEDIN,
  },
];

const connectButtonId = (platform) => `automation-connect-${platform}`;

const StepPlatforms = ({
  formState,
  onChange,
  businessName,
  allowedPlatforms = null,
  onLockedCapability = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { accounts, isLoading } = useSharePlatformAccounts();
  const buttonActions = useButtonActionPlatform(false);

  const onToggleAccount = (account, checked) => {
    const locked =
      checked &&
      Array.isArray(allowedPlatforms) &&
      !allowedPlatforms.includes(account.platform);
    if (locked) {
      onLockedCapability();
      return;
    }
    onChange({
      platforms: {
        ...formState.platforms,
        [account.key]: checked,
      },
    });
  };

  const onRefreshAccounts = () => {
    dispatch(startGetIntegrations());
  };

  const onSelectConnectPlatform = (platform) => {
    document.getElementById(connectButtonId(platform))?.click();
  };

  return (
    <AutomationStepSection
      title={t(AUTOMATIONS_STEP_PLATFORMS_TITLE)}
      description={t(AUTOMATIONS_STEP_PLATFORMS_HELP, {
        NAME_BUSINESS: businessName,
      })}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}>
          <ButtonMenu
            mode="button"
            colorButton="secondary"
            fullWidth
            textButton={AUTOMATIONS_CONNECT_ACCOUNTS}
            options={CONNECT_ACCOUNT_OPTIONS.map((option) => ({
              label: option.label,
              icon: (
                <Box sx={{ display: "flex", mr: 1 }}>
                  <PlatformLogo
                    platform={option.platform}
                    width={20}
                    height={20}
                    alt={option.platform}
                  />
                </Box>
              ),
              onClick: () => onSelectConnectPlatform(option.platform),
            }))}
            sx={{
              height: 40,
              minHeight: 40,
              textTransform: "none",
            }}
          />
        </Box>

        {CONNECT_ACCOUNT_OPTIONS.map((option) =>
          buttonActions[option.actionKey]
            ? React.cloneElement(buttonActions[option.actionKey], {
                key: option.platform,
                id: connectButtonId(option.platform),
                mode: "add",
                onClick: onRefreshAccounts,
                sx: { display: "none" },
              })
            : null
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : accounts.length === 0 ? (
          <Box
            sx={{
              ...subtleBorderSx,
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t(CONNECTED_ACCOUNTS_EMPTY_HINT)}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {accounts.map((account) => (
              <Box
                key={account.key}
                sx={{
                  ...subtleBorderSx,
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                  minWidth: 0,
                }}
              >
                <Checkbox
                  checked={Boolean(formState.platforms[account.key])}
                  onChange={(e) => onToggleAccount(account, e.target.checked)}
                  size="small"
                />
                <PlatformLogo
                  platform={account.platform}
                  width={32}
                  height={32}
                  alt={account.platform}
                />
                <Box
                  sx={{
                    flex: "1 1 140px",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ lineHeight: 1.35, overflowWrap: "anywhere" }}
                  >
                    {t(PLATFORM_NAME_KEYS[account.platform])}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{
                      lineHeight: 1.35,
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {account.handle}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "success.main",
                      display: { xs: "block", sm: "none" },
                    }}
                  >
                    ● {t(AUTOMATIONS_PLATFORM_CONNECTED)}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "success.main",
                    whiteSpace: "nowrap",
                    display: { xs: "none", sm: "block" },
                    flexShrink: 0,
                  }}
                >
                  ● {t(AUTOMATIONS_PLATFORM_CONNECTED)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </AutomationStepSection>
  );
};

export default StepPlatforms;
